/**
 * Spec BUG-044 — structured cross-reference form for section 02.
 *
 * Patent cross-reference paragraphs are a legal field, not free
 * prose ("This application claims priority to U.S. Provisional
 * Application No. 63/123,456, filed January 15, 2024."). The
 * standard Generate / Steering toolbar isn't right for it.
 * Instead the drafter fills:
 *   - Relationship type (continuation / cip / divisional /
 *                       priority / none)
 *   - Prior application type (US Provisional / US Non-Prov /
 *                             PCT / EP / GB / Other)
 *   - Application number (free text)
 *   - Filing date (free text — drafter formats per jurisdiction)
 *
 * Multiple applications via "+ Add another". Submit calls
 * ``postSpecCrossReferenceGenerate`` which:
 *   - Persists the form payload (spec_section_settings.custom_
 *     settings_json) so the form rehydrates on next mount.
 *   - Renders the jurisdiction-correct sentence(s) and saves as
 *     the section's paragraph (overwriting any existing).
 *
 * "Write manually" toggle hides the form and lets the drafter
 * use the standard paragraph editor — escape hatch for cases
 * the form doesn't cover.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil } from 'lucide-react';
import {
  getSpecCrossReferenceForm,
  postSpecCrossReferenceGenerate,
  type SpecCrossReferenceApplication,
  type SpecSectionRow,
} from '@/api/client';
import { useSpecStore } from '@/store/specStore';

const _RELATIONSHIPS: { value: string; label: string }[] = [
  { value: 'continuation', label: 'Continuation of' },
  { value: 'cip',          label: 'Continuation-in-part of' },
  { value: 'divisional',   label: 'Divisional of' },
  { value: 'priority',     label: 'Claims priority to' },
  { value: 'none',         label: 'None (omit this row)' },
];
const _PRIOR_TYPES: { value: string; label: string }[] = [
  { value: 'us_provisional',    label: 'US Provisional' },
  { value: 'us_nonprovisional', label: 'US Non-Provisional' },
  { value: 'pct',               label: 'PCT' },
  { value: 'ep',                label: 'EP' },
  { value: 'gb',                label: 'GB' },
  { value: 'other',             label: 'Other' },
];

function _emptyApp(): SpecCrossReferenceApplication {
  return {
    relationship: 'priority',
    prior_type: 'us_provisional',
    number: '',
    filing_date: '',
  };
}

interface Props {
  matterId: string;
  section: SpecSectionRow;
  /** When true, the standard ParagraphBlock editor takes over —
   * drafter is in "Write manually" mode. */
  manualMode: boolean;
  onToggleManual: () => void;
}

export default function CrossReferenceForm({
  matterId, section, manualMode, onToggleManual,
}: Props): JSX.Element {
  const [applications, setApplications] = useState<SpecCrossReferenceApplication[]>(
    [_emptyApp()],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const showToast = useSpecStore((s) => s.showToast);

  // Hydrate from persisted form data on mount / section change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSpecCrossReferenceForm(matterId, section.id)
      .then((r) => {
        if (cancelled) return;
        if (r.applications && r.applications.length > 0) {
          setApplications(r.applications);
        } else {
          setApplications([_emptyApp()]);
        }
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          (e as { message?: string })?.message
            || 'Failed to load form.',
        );
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [matterId, section.id]);

  const updateApp = useCallback(
    (idx: number, patch: Partial<SpecCrossReferenceApplication>) => {
      setApplications((prev) => prev.map(
        (a, i) => (i === idx ? { ...a, ...patch } : a),
      ));
    }, [],
  );

  const onAdd = useCallback(() => {
    setApplications((prev) => [...prev, _emptyApp()]);
  }, []);
  const onRemove = useCallback((idx: number) => {
    setApplications((prev) => {
      if (prev.length <= 1) return [_emptyApp()];
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const onGenerate = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await postSpecCrossReferenceGenerate(
        matterId, section.id, applications,
      );
      // Refresh the section's paragraphs in the store so the
      // generated text shows immediately under the form (or in
      // manual-mode after toggle).
      useSpecStore.setState((s) => ({
        paragraphsBySection: {
          ...s.paragraphsBySection,
          [section.id]: r.paragraphs,
        },
      }));
      showToast('Cross-reference paragraph generated.');
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message
        || 'Generate failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [matterId, section.id, applications, submitting, showToast]);

  if (loading) {
    return (
      <div
        className="mt-6 flex items-center gap-2 text-xs text-zinc-500"
        data-testid="spec-cross-ref-loading"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading form…
      </div>
    );
  }

  if (manualMode) {
    return (
      <div className="mt-2 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600">
        Free-form mode is active. Use the paragraph editor above
        to write the cross-reference manually, or
        {' '}
        <button
          type="button"
          className="underline underline-offset-2 hover:text-zinc-900"
          onClick={onToggleManual}
          data-testid="spec-cross-ref-manual-toggle-back"
        >
          switch back to the form
        </button>
        .
      </div>
    );
  }

  return (
    <div
      className="mt-6 space-y-3 rounded-sm border border-zinc-200 bg-white p-4"
      data-testid="spec-cross-ref-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-800">
          Cross-reference details
        </h3>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900"
          onClick={onToggleManual}
          data-testid="spec-cross-ref-manual-toggle"
          title="Skip the form and write the paragraph by hand."
        >
          <Pencil className="h-3 w-3" />
          Write manually
        </button>
      </div>

      <ul className="space-y-3">
        {applications.map((app, idx) => (
          <li
            key={idx}
            className="rounded-sm border border-zinc-200 bg-zinc-50 p-3"
            data-testid={`spec-cross-ref-app-${idx}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] font-mono uppercase tracking-wide text-zinc-500">
                Application #{idx + 1}
              </span>
              {applications.length > 1 && (
                <button
                  type="button"
                  className="text-zinc-400 hover:text-rose-600"
                  onClick={() => onRemove(idx)}
                  aria-label="Remove this application"
                  title="Remove"
                  data-testid={`spec-cross-ref-remove-${idx}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-zinc-600">
                  Relationship
                </span>
                <select
                  className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-xs"
                  value={app.relationship}
                  onChange={(e) => updateApp(idx, { relationship: e.target.value })}
                  data-testid={`spec-cross-ref-relationship-${idx}`}
                >
                  {_RELATIONSHIPS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-zinc-600">
                  Prior application type
                </span>
                <select
                  className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-xs"
                  value={app.prior_type}
                  onChange={(e) => updateApp(idx, { prior_type: e.target.value })}
                  data-testid={`spec-cross-ref-prior-type-${idx}`}
                >
                  {_PRIOR_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-zinc-600">
                  Application number
                </span>
                <input
                  type="text"
                  className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-xs font-mono"
                  value={app.number}
                  placeholder="e.g. 63/123,456"
                  onChange={(e) => updateApp(idx, { number: e.target.value })}
                  data-testid={`spec-cross-ref-number-${idx}`}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium text-zinc-600">
                  Filing date
                </span>
                <input
                  type="text"
                  className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-xs"
                  value={app.filing_date}
                  placeholder="e.g. January 15, 2024"
                  onChange={(e) => updateApp(idx, { filing_date: e.target.value })}
                  data-testid={`spec-cross-ref-date-${idx}`}
                />
              </label>
            </div>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#002FA7] hover:text-[#001f6f]"
        onClick={onAdd}
        data-testid="spec-cross-ref-add"
      >
        <Plus className="h-3 w-3" />
        Add another application
      </button>

      {error && (
        <p
          className="rounded-sm border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700"
          role="alert"
          data-testid="spec-cross-ref-error"
        >
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-2">
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-sm bg-[#002FA7] px-3 text-xs font-medium text-white hover:bg-[#001f6f] disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onGenerate}
          disabled={submitting}
          data-testid="spec-cross-ref-generate"
        >
          {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
          Generate from form
        </button>
      </div>
    </div>
  );
}
